<template>
  <b-modal no-fade v-model="dialog.show" size="lg" ref="ticket-modal"
           :hide-footer="true" centered no-close-on-backdrop
           @close="onDialogClose"
  >
    <template #modal-title>
      {{ isNew ? $t('tickets.newTicket') : ticket.title }}
    </template>

    <div v-if="loading" class="text-center p-3">
      <b-spinner></b-spinner>
    </div>

    <div v-else>
      <!-- Ticket Form -->
      <b-form @submit.prevent="onSubmit">
        <b-row>
          <b-col md="12">
            <b-form-group :label="$t('columns.title')">
              <b-form-input v-model="ticket.title" required :disabled="!isNew && !isAdmin"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group :label="$t('columns.description')">
              <b-form-textarea v-model="ticket.description" rows="4" required :disabled="!isNew && !isAdmin"></b-form-textarea>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group :label="$t('columns.type')">
              <b-form-select v-model="ticket.type" :options="typeOptions" required :disabled="!isNew && !isAdmin"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('columns.priority')">
              <b-form-select v-model="ticket.priority" :options="priorityOptions" :disabled="!isNew && !isAdmin"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col :md="isAdminUser ? '6' : '12'">
            <b-form-group :label="$t('tickets.participants')">
                <user-picker multiple v-model="ticket.participants" />
            </b-form-group>
          </b-col>
          <b-col v-if="isAdminUser" md="6">
            <b-form-group :label="$t('columns.createdBy')">
              <user-picker v-model="ticket.createdBy" />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Admin-only fields -->
        <b-row v-if="!isNew && isAdmin">
          <b-col md="6">
            <b-form-group :label="$t('columns.status')">
              <b-form-select v-model="ticket.status" :options="statusOptions"></b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group :label="$t('tickets.assignedTo')">
              <b-form-input v-model="ticket.assignedTo"></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>



        <!-- File Upload -->
        <b-row v-if="isNew">
          <b-col md="12">
            <b-form-group :label="$t('tickets.attachments')">
              <b-form-file v-model="files" multiple :placeholder="$t('tickets.chooseFiles')" drop-placeholder="Drop files here..."></b-form-file>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Info (read-only when editing) -->
        <b-row v-if="!isNew">
          <b-col md="6">
            <small class="text-muted">{{ $t('columns.createdBy') }}: {{ ticket.createdByName }}</small>
          </b-col>
          <b-col md="6">
            <small class="text-muted">{{ $t('columns.creationTime') }}: {{ formatDate(ticket.creationTime) }}</small>
          </b-col>
        </b-row>

        <!-- Existing Attachments -->
        <div v-if="!isNew && ticket.attachments && ticket.attachments.length > 0" class="mt-3">
          <h6>{{ $t('tickets.attachments') }}</h6>
          <div v-for="(att, idx) in ticket.attachments" :key="idx" class="mb-1">
            <b-link @click="downloadFile(att.fileName)">
              <i class="fas fa-paperclip"></i> {{ att.originalName }}
            </b-link>
            <small class="text-muted ml-2">({{ formatFileSize(att.size) }})</small>
          </div>
        </div>

        <hr/>

        <div class="text-right">
          <b-button variant="outline-secondary" class="mr-1" @click="closeModal">{{ $t('operations.cancel') }}</b-button>
          <b-button variant="primary" type="submit" :disabled="submitting">
            <b-spinner small v-if="submitting"></b-spinner>
            {{ isNew ? $t('operations.create') : $t('operations.save') }}
          </b-button>
        </div>
      </b-form>

      <!-- Comments Section (only for existing tickets) -->
      <div v-if="!isNew" class="mt-4">
        <h5>{{ $t('tickets.comments') }}</h5>
        <div v-if="ticket.comments && ticket.comments.length > 0" class="mb-3" style="max-height: 400px; overflow-y: auto;">
          <div v-for="(comment, idx) in ticket.comments" :key="idx"
               class="p-2 mb-2 rounded"
               :class="comment.userId === currentUserId ? 'bg-light-primary' : 'bg-light'"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ comment.userName }}</strong>
              <small class="text-muted">{{ formatDate(comment.timestamp) }}</small>
            </div>
            <p class="mb-1 mt-1">{{ comment.message }}</p>
            <!-- Comment attachments -->
            <div v-if="comment.attachments && comment.attachments.length > 0">
              <div v-for="(att, aidx) in comment.attachments" :key="aidx">
                <b-link @click="downloadFile(att.fileName)" class="small">
                  <i class="fas fa-paperclip"></i> {{ att.originalName }}
                </b-link>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="text-muted mb-3">{{ $t('tickets.noComments') }}</div>

        <!-- Add Comment -->
        <b-form @submit.prevent="onAddComment">
          <b-form-group>
            <b-form-textarea v-model="newComment" rows="2" :placeholder="$t('tickets.addComment')"></b-form-textarea>
          </b-form-group>
          <b-form-group>
            <b-form-file v-model="commentFiles" multiple :placeholder="$t('tickets.attachFiles')" size="sm"></b-form-file>
          </b-form-group>
          <b-button variant="outline-primary" type="submit" size="sm" :disabled="!newComment || submittingComment">
            <b-spinner small v-if="submittingComment"></b-spinner>
            {{ $t('tickets.sendComment') }}
          </b-button>
        </b-form>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import mixinBase from '@/components/mixin/mixinBase'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import mixinPermissions from '@/components/mixin/mixinPermissions'
import Ticket from '@/model/ticket/Ticket'
import Permissions from '@/model/Permissions'
import UserPicker from '@/components/widget/picker/userPicker.vue'

export default {
  name: 'TicketModal',
  components: { UserPicker },
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  data: () => ({
    dialog: { show: false },
    loading: false,
    submitting: false,
    submittingComment: false,
    isNew: true,
    ticket: Ticket(),
    files: [],
    newComment: '',
    commentFiles: [],
    typeOptions: [
      { value: null, text: '-- Select type --' },
      { value: 'NEW_FEATURE', text: 'New Feature' },
      { value: 'BUG', text: 'Bug' },
      { value: 'CLAIM', text: 'Claim' },
      { value: 'TASK', text: 'Task' },
    ],
    priorityOptions: [
      { value: null, text: '-- Select priority --' },
      { value: 'LOW', text: 'Low' },
      { value: 'MEDIUM', text: 'Medium' },
      { value: 'HIGH', text: 'High' },
      { value: 'CRITICAL', text: 'Critical' },
    ],
    statusOptions: [
      { value: 'REQUEST', text: 'Request' },
      { value: 'IN_PROGRESS', text: 'In Progress' },
      { value: 'WAITING_FOR_USER_RESPONSE', text: 'Waiting for User' },
      { value: 'DONE', text: 'Done' },
      { value: 'CANCELLED', text: 'Cancelled' },
    ],
  }),
  created() {
    this.refreshTables();
  },
  computed: {
    ...mapGetters('user', ['myId','isAdminUser']),
    isAdmin() {
      return this.hasPermission(Permissions.TICKET_UPDATE)
    },
    currentUserId() {
      return this.myId
    },
  },
  methods: {
    ...mapActions('ticket', ['getTicketById', 'createTicket', 'updateTicket', 'addComment', 'downloadAttachment']),
    showModal(id) {
      this.isNew = id == null
      this.loading = true
      this.dialog.show = true
      this.files = []
      this.newComment = ''
      this.commentFiles = []

      if (this.isNew) {
        this.ticket = Ticket()
        this.ticket.priority = 'MEDIUM'
        this.loading = false
      } else {
        this.getTicketById(id)
          .then(ticket => {
            this.ticket = ticket
            this.loading = false
          })
          .catch(err => {
            this.showErrorNotification('Error', this.getErrorDescription(err))
            this.loading = false
          })
      }
    },
    closeModal() {
      this.dialog.show = false
      this.ticket = Ticket()
    },
    onDialogClose() {
      this.ticket = Ticket()
    },
    onSubmit() {
      this.submitting = true
      let $this = this
      if (this.isNew) {
        this.createTicket({ ticket: this.ticket, files: this.files })
          .then(() => {
            $this.showSuccessNotification($this.$t('tickets.ticketCreated'))
            $this.closeModal()
            $this.$emit('submitted')
          })
          .catch(err => {
            $this.showErrorNotification('Error', $this.getErrorDescription(err))
          })
          .finally(() => {
            $this.submitting = false
          })
      } else {
        this.updateTicket({ id: this.ticket.id, ticket: this.ticket })
          .then(() => {
            $this.showSuccessNotification($this.$t('tickets.ticketUpdated'))
            $this.closeModal()
            $this.$emit('submitted')
          })
          .catch(err => {
            $this.showErrorNotification('Error', $this.getErrorDescription(err))
          })
          .finally(() => {
            $this.submitting = false
          })
      }
    },
    onAddComment() {
      this.submittingComment = true
      let $this = this
      this.addComment({ id: this.ticket.id, message: this.newComment, files: this.commentFiles })
        .then(ticket => {
          $this.ticket = ticket
          $this.newComment = ''
          $this.commentFiles = []
          $this.showSuccessNotification($this.$t('tickets.commentAdded'))
        })
        .catch(err => {
          $this.showErrorNotification('Error', $this.getErrorDescription(err))
        })
        .finally(() => {
          $this.submittingComment = false
        })
    },
    downloadFile(fileName) {
      this.downloadAttachment({ ticketId: this.ticket.id, fileName })
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleString()
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
    },
  }
}
</script>

<style scoped>
.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
}
</style>
